import React, { FunctionComponent, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MuiAvatar from "@mui/material/Avatar";
import MuiDivider from "@mui/material/Divider";
import DotsVertical from "mdi-material-ui/DotsVertical";
import { useDispatch } from "react-redux";
import { Icon } from "@hxnova/icons";

import "../../../css/nav_bar/nav_bar_kebab_menu.css";
import NavBarKebabItemLogout from "./kebab_menu_items/nav_bar_kebab_item_logout";
import usePopover from "../../services/component_helpers/component_effects/use_popover";
import { useFlags } from "../../services/utilities/launch_darkly_helper";
import { connect } from "react-redux";
import getUserAccount from "../../services/getters/user_getters/get_user_account";
import getUserEmail from "../../services/getters/user_getters/get_user_email";
import getProjectIdFromLocation from "../../services/getters/location_metadata/get_project_id_from_location";
import getOrganizationId from "../../services/getters/organization_getters/get_organization_id";
import getProjectName from "../../services/getters/project_getters/get_project_name";
import getOrganizationName from "../../services/getters/organization_getters/get_organization_name";
import toProfilePage, { TO_PROFILE_PAGE } from "../../events/routing/to_profile_page";
import toSupportCenterPage, { TO_SUPPORT_CENTER_PAGE } from "../../events/routing/to_support_center_page";
import toOrganizationSupportCenterPage, { TO_ORGANIZATION_SUPPORT_CENTER_PAGE } from "../../events/routing/to_organization_support_center_page";

import { PROFILE_BACK_ORIGIN_STORAGE_KEY } from "../../models/domain/back_context";
import type { BackContext } from "../../models/domain/back_context";


function getInitials(name: string): string {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Captures the page the user is leaving (its routing action) plus a human label, so an org-level
// page (Support Center, User Profile) can offer a back button to return here. Returns undefined
// when there is no project/org context to return to (an org/project-agnostic origin).
function captureBackContext(
  location: { type?: string, payload?: Record<string, any>, query?: Record<string, any> } | null | undefined,
  projectId: string | null | undefined,
  organizationId: string | null | undefined,
  projectName: string | null | undefined,
  organizationName: string | null | undefined
): BackContext | undefined {
  if (!projectId && !organizationId) {
    return undefined;
  }
  const query = location?.query;
  return {
    label: projectId ? (projectName || "Project") : (organizationName || "Organization"),
    backRouteType: location?.type as string,
    backRoutePayload: location?.payload ?? {},
    ...(query && Object.keys(query).length > 0 ? { backRouteQuery: query } : {}),
  };
}

export type Props = ReturnType<typeof mapStateToProps> & { children?: React.ReactNode };

export const NavBarKebabMenu: FunctionComponent<Props> = (props) => {
  const {
    children,
    userEmail,
    userAccountName,
    projectId,
    organizationId,
    projectName,
    organizationName,
    location,
    backContext: currentBackContext,
  } = props;

  const [open, anchorEl, handleOpen, onClose] = usePopover();
  const initials = getInitials(userAccountName || userEmail || "");
  const { userProfileFeatureEnabled, supportCenterEnabled } = useFlags();
  const dispatch = useDispatch();

  // Re-opening Support Center while already on it must not re-capture the current (Support Center)
  // location — that would point the back button at itself and make it a no-op. Preserve the
  // backContext captured on the way in; the menu still closes via the Menu's onClose handler.
  const handleSupportCenterClick = useCallback(() => {
    if (location?.type === TO_SUPPORT_CENTER_PAGE || location?.type === TO_ORGANIZATION_SUPPORT_CENTER_PAGE) {
      return;
    }
    const backContext = captureBackContext(location, projectId, organizationId, projectName, organizationName);
    dispatch(projectId
      ? toSupportCenterPage(projectId, backContext)
      : toOrganizationSupportCenterPage(organizationId, backContext));
  }, [dispatch, projectId, organizationId, projectName, organizationName, location]);

  // The Profile URL carries no project/org id, so there is nothing to recover on a cold load: with
  // no project/org context (agnostic origin, or a direct /profile load) captureBackContext returns
  // undefined and the page shows no back button. Re-opening Profile while already on it preserves
  // the captured target.
  const handleProfileClick = useCallback(() => {
    if (location?.type === TO_PROFILE_PAGE) {
      return;
    }

    // Support Center is itself a kebab-reached page that carries the scope id in its URL. Opening
    // Profile from it must not capture Support Center as the target — that would label the button with
    // the scope name while actually navigating to Support Center. Instead inherit the exact page that
    // Support Center itself came from, so Profile's back returns there directly (one hop, label matches).
    const openedFromSupportCenter = location?.type === TO_SUPPORT_CENTER_PAGE || location?.type === TO_ORGANIZATION_SUPPORT_CENTER_PAGE;
    const backContext = openedFromSupportCenter
      ? currentBackContext ?? undefined
      : captureBackContext(location, projectId, organizationId, projectName, organizationName);

    // Persist the scope we came from so the back button survives a reload of the id-less /profile URL.
    // Keyed on the scope (not on backContext) so a cold-loaded Support Center — which has a scope but no
    // captured exact page — still leaves Profile a scope-home fallback. Session-scoped (not localStorage)
    // so it never resurfaces as a stale button on a fresh login; cleared here when the origin is
    // agnostic, and on logout.
    if (projectId || organizationId) {
      window.sessionStorage.setItem(PROFILE_BACK_ORIGIN_STORAGE_KEY, JSON.stringify({ projectId: projectId ?? null, organizationId: organizationId ?? null }));
    } else {
      window.sessionStorage.removeItem(PROFILE_BACK_ORIGIN_STORAGE_KEY);
    }
    dispatch(toProfilePage(backContext));
  }, [dispatch, projectId, organizationId, projectName, organizationName, location, currentBackContext]);

  // One Support Center entry shared by both menu variants, so the legacy menu can't drift from the
  // avatar menu: same back-context capture, same availability gate. Hidden when the page isn't
  // reachable (flag off, or no scope in the URL) rather than linking to a 404. The icon is
  // profile-menu only, matching NavBarKebabItemLogout — its styling is scoped to --profile.
  const supportCenterItem = supportCenterEnabled && (projectId || organizationId)
    ? <MenuItem key="support" className="NavBarKebabMenu-menuItem" onClick={handleSupportCenterClick}>
      {userProfileFeatureEnabled && <Icon name="help_center" family="material" className="NavBarKebabMenu-menuIcon"/>}
      Support Center
    </MenuItem>
    : null;

  return <><IconButton
    className={`NavBarKebabMenu-button${userProfileFeatureEnabled ? " NavBarKebabMenu-button--profile" : ""}`}
    onClick={handleOpen}
    aria-haspopup="true"
    aria-controls="kebab-menu"
  >
    {userProfileFeatureEnabled
      ? <MuiAvatar className="NavBarKebabMenu-avatarButton">{initials}</MuiAvatar>
      : <DotsVertical/>}
  </IconButton>
    <Menu
      className={`NavBarKebabMenu${userProfileFeatureEnabled ? " NavBarKebabMenu--profile" : ""}`}
      id="kebab-menu"
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: userProfileFeatureEnabled ? "right" : "left"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: userProfileFeatureEnabled ? "right" : "left"
      }}
      onClose={onClose}
      onClick={onClose}
    >
      {userProfileFeatureEnabled ? [
        <div key="header" className="NavBarKebabMenu-header">
          <MuiAvatar className="NavBarKebabMenu-headerAvatar">{initials}</MuiAvatar>
          <div className="NavBarKebabMenu-userInfo">
            <span className="NavBarKebabMenu-userName">{userAccountName}</span>
            <span className="NavBarKebabMenu-userEmail">{userEmail}</span>
          </div>
        </div>,
        <MuiDivider key="divider-top"/>,
        children,
        <MenuItem key="profile" className="NavBarKebabMenu-menuItem" onClick={handleProfileClick}>
          <Icon name="account_circle" family="material" className="NavBarKebabMenu-menuIcon"/>
          Profile
        </MenuItem>,
        supportCenterItem,
        <MuiDivider key="divider-bottom"/>,
        <NavBarKebabItemLogout key="logout"/>,
      ] : [
        children,
        <MuiDivider key="divider"/>,
        <ListSubheader key="email" disableSticky className="NavBarKebabMenu-label NavBarKebabMenu-userEmail">
          {userEmail}
        </ListSubheader>,
        supportCenterItem,
        <NavBarKebabItemLogout key="logout"/>,
      ]}
    </Menu>
  </>;
};

const mapStateToProps = (state, props) => ({
  userEmail: getUserEmail(state, props),
  userAccountName: getUserAccount(state, props)?.name || null,
  projectId: getProjectIdFromLocation(state, props),
  organizationId: getOrganizationId(state, props),
  projectName: getProjectName(state, props),
  organizationName: getOrganizationName(state, props),
  location: state.location,
  backContext: state.locationMetadata.backContext ?? null,
});


export default connect(mapStateToProps)(NavBarKebabMenu);
